import { makeStyles } from '@material-ui/core/styles';
import typography from '@/theme/typography';
import {
  BackgroundColors,
  PrimaryColors,
  SecondaryColors,
  SurfaceColors,
  TextColors,
  TextOpacities,
} from '@/utils/constants';
import { hexToRGBA } from '@/utils/helpers';

const lightTheme = {
  root: {
    background: SurfaceColors.light,

    '& $content': {
      color: hexToRGBA(PrimaryColors.main, TextOpacities.light2),
    },
    '& $title': {
      color: PrimaryColors.main,
    },
    '& $description': {
      color: TextColors.dark,
    },
    '& $certified small': {
      color: hexToRGBA(TextColors.dark, TextOpacities.light2),
    },
  },
};

const darkTheme = {
  root: {
    background: PrimaryColors.light,
    '& $content': {
      color: hexToRGBA(TextColors.light, TextOpacities.light2),
    },
    '& $title': {
      color: TextColors.light,
    },
    '& $description': {
      color: TextColors.light,
    },
    '& $certified small': {
      color: hexToRGBA(TextColors.light, TextOpacities.light2),
    },
  },
};

const useStyles = makeStyles(
  (muiTheme) => ({
    root: {
      '&.light-theme': lightTheme.root,
      '&.dark-theme': darkTheme.root,
      borderRadius: '4px',
      overflow: 'hidden',
      width: '100%',
      height: '100%',

      '&.MuiCard-root:not(:hover):not(:focus-within)': {
        boxShadow: 'none',
      },
    },
    button: {
      textAlign: 'left',
      width: '100%',
      height: '100%',
    },
    content: {
      display: 'flex',
      padding: muiTheme.spacing(4),
      flexDirection: 'column',
      '& .MuiChip-root': {
        maxWidth: '100%',
      },
    },
    image: {
      height: '184px',
      position: 'relative',

      '&:after': {
        content: '""',
        position: 'absolute',
        width: '100%',
        height: '100%',
        top: 0,
        left: 0,
        borderRadius: '4px 4px 0 0',
        border: '1px solid rgba(0, 0, 0, .05)',
        borderBottom: 'none',
      },

      '& > img': {
        height: '100%',
        width: '100%',
        objectFit: 'cover',
      },
    },
    header: { marginBottom: muiTheme.spacing(1) },
    skeleton: {
      background: BackgroundColors.light,
      height: '11px',
      width: '100%',

      '&:before': {
        content: '""',
        fontSize: 'inherit',
        lineHeight: 'inherit',
        display: 'block',
      },

      '&.is-image': {
        height: '184px',
      },
    },
    title: {
      '&.MuiTypography-root': {
        marginBottom: muiTheme.spacing(1),
      },
      '& .skeleton': {
        marginTop: '9px',
        marginBottom: '16px',
        height: '16px',
        width: '75%',

        [muiTheme.breakpoints.up('md')]: {
          width: '30%',
        },
      },
    },
    description: {
      ...typography.body1,
      marginBottom: muiTheme.spacing(1),

      '& .skeleton': {
        '& + *': {
          marginTop: '14px',
        },

        '&:last-child': {
          width: '50%',
        },
      },
      '& h1,h2,h3,h4,h5': {
        color: 'inherit',
      },
    },
    certified: {
      marginTop: muiTheme.spacing(1),

      '&:not(:last-child)': {
        marginBottom: muiTheme.spacing(1),
      },

      '& img': {
        height: '13px',
        width: 'auto',
        marginTop: '3px',
        marginBottom: '-1px',
        display: 'inline',
      },
    },
    spacer: {
      flex: 1,
      marginTop: muiTheme.spacing(1),
    },
    chip: {
      marginBottom: muiTheme.spacing(1),
      '& .MuiChip-label': {
        color: `${SecondaryColors.dark} !important`,
      },
    },
    footer: {
      '& .skeleton': {
        width: '140px',
        marginTop: '22px',
      },
    },
    rootSkeleton: {
      '&.animated': {
        '&:not(.light-theme) .skeleton': {
          animation: `$skeletonCard 1000ms ${muiTheme.transitions.easing.easeInOut} infinite`,
        },

        '&.light-theme .skeleton': {
          animation: `$skeletonCardDark 1000ms ${muiTheme.transitions.easing.easeInOut} infinite`,
        },
      },
    },
    '@keyframes skeletonCard': {
      '0%, 100%': {
        opacity: 0.75,
      },
      '50%': {
        opacity: 1,
      },
    },
    '@keyframes skeletonCardDark': {
      '0%, 100%': {
        opacity: 0.25,
      },
      '50%': {
        opacity: 0.5,
      },
    },
  }),
  { name: 'GenMarketplaceCard' },
);

export default useStyles;
